const CANVAS_SIZE = 64;
const MAX_DISPLAY_COUNT = 99;
const BADGE_COLOR = '#E5484D';
const ICON_LIGHT = '/brand-assets/corvus_icon_purple.png';
const ICON_DARK = '/brand-assets/corvus_icon_white.png';

let currentCount = 0;
let baseTitle = null;
let originalHrefs = null;
let themeListenerAttached = false;
const iconCache = {};

const getFaviconLinks = () =>
  document.querySelectorAll('link#favicon, link.favicon');

const isLightTheme = () =>
  window.matchMedia?.('(prefers-color-scheme: light)').matches;

export const formatFaviconCount = count =>
  count > MAX_DISPLAY_COUNT ? `${MAX_DISPLAY_COUNT}+` : String(count);

const loadIcon = src => {
  if (!iconCache[src]) {
    iconCache[src] = new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = src;
    });
  }
  return iconCache[src];
};

const drawBadge = (context, label) => {
  const isSingleDigit = label.length === 1;
  const height = CANVAS_SIZE * 0.62;
  const width = isSingleDigit ? height : CANVAS_SIZE * 0.92;
  const x = CANVAS_SIZE - width;
  const y = CANVAS_SIZE - height;
  const fontSize = { 1: 0.52, 2: 0.46, 3: 0.36 }[label.length] * CANVAS_SIZE;

  context.fillStyle = BADGE_COLOR;
  context.beginPath();
  context.roundRect(x, y, width, height, height / 2);
  context.fill();

  context.fillStyle = '#FFFFFF';
  context.font = `800 ${fontSize}px sans-serif`;
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText(label, x + width / 2, y + height / 2 + fontSize * 0.04);
};

const drawFavicon = async count => {
  const icon = await loadIcon(isLightTheme() ? ICON_LIGHT : ICON_DARK);

  const canvas = document.createElement('canvas');
  canvas.width = CANVAS_SIZE;
  canvas.height = CANVAS_SIZE;
  const context = canvas.getContext('2d');

  const scale = Math.min(CANVAS_SIZE / icon.width, CANVAS_SIZE / icon.height);
  context.drawImage(
    icon,
    (CANVAS_SIZE - icon.width * scale) / 2,
    (CANVAS_SIZE - icon.height * scale) / 2,
    icon.width * scale,
    icon.height * scale
  );
  drawBadge(context, formatFaviconCount(count));

  return canvas.toDataURL('image/png');
};

const restoreFavicons = () => {
  if (!originalHrefs) return;

  getFaviconLinks().forEach(link => {
    const original = originalHrefs.get(link);
    if (original) link.href = original;
  });
  // The layout script keeps #favicon in sync with the theme, so use the current one.
  const themedLink = document.getElementById('favicon');
  if (themedLink) themedLink.href = isLightTheme() ? ICON_LIGHT : ICON_DARK;
  originalHrefs = null;
};

const renderFavicon = async () => {
  const count = currentCount;
  if (count <= 0) {
    restoreFavicons();
    return;
  }

  try {
    const dataUrl = await drawFavicon(count);
    // A newer count may have arrived while the icon was loading.
    if (count !== currentCount) return;

    const links = getFaviconLinks();
    if (!originalHrefs) {
      originalHrefs = new Map([...links].map(link => [link, link.href]));
    }
    links.forEach(link => {
      link.href = dataUrl;
    });
  } catch (error) {
    // Keep the static favicon if the icon cannot be drawn.
  }
};

const attachThemeListener = () => {
  if (themeListenerAttached || !window.matchMedia) return;

  themeListenerAttached = true;
  window
    .matchMedia('(prefers-color-scheme: light)')
    .addEventListener('change', renderFavicon);
};

export const setFaviconCount = count => {
  const parsedCount = Number(count);
  currentCount =
    Number.isFinite(parsedCount) && parsedCount > 0
      ? Math.floor(parsedCount)
      : 0;
  attachThemeListener();
  return renderFavicon();
};

export const setTabTitleCount = count => {
  baseTitle ??= document.title.trim();
  const parsedCount = Math.floor(Number(count));
  document.title =
    parsedCount > 0
      ? `(${formatFaviconCount(parsedCount)}) ${baseTitle}`
      : baseTitle;
};
